'use client'

// Live "Weekly Engagement Trends" (2026-08-26): the dashboard trendingNow block keeps
// its curated dump cards as skeleton + fallback, but each card's links are replaced with
// the content members actually viewed most in the trailing 7 days (get_trending_content
// RPC, aggregated over user_content_engagement, recomputed on every page load — a rolling
// window, not a scheduled refresh). Card buckets by position:
//   0 = processes (workflow hubs), 1 = process steps (stages),
//   2 = equipment & suppliers (technology guides, products, vendors).
// A bucket with fewer than 2 live entries keeps its curated links — cold-start safe, and
// failures degrade to the static cards, never a broken section.
import React, { useEffect, useState } from 'react'
import { TrendingNow, type TrendingNowProps } from '@/ui/components/TrendingNow'
import { SUPABASE_CONFIGURED, supabaseBrowser } from '@/lib/supabase/client'

type TrendingRow = {
  content_path: string
  title: string
  content_type: string
  workflow: string | null
  viewers: number
  views: number
}

const LINKS_PER_CARD = 3

const isHub = (p: string) => /^\/workflows\/[^/]+\/[^/]+$/.test(p) && !p.endsWith('/technology')
const isStage = (p: string) =>
  /^\/workflows\/[^/]+\/[^/]+\/[^/]+$/.test(p) && !p.includes('/technology/')
const isEquipment = (r: TrendingRow) =>
  r.content_path.includes('/technology/') ||
  r.content_type === 'product' ||
  r.content_type === 'vendor'

// stage titles carry a " — <workflow>" suffix; the card is compact, drop it
const displayTitle = (r: TrendingRow) =>
  r.workflow && r.title.endsWith(` — ${r.workflow}`)
    ? r.title.slice(0, -(r.workflow.length + 3))
    : r.title

export const TrendingLive: React.FC<TrendingNowProps> = (props) => {
  const [cards, setCards] = useState(props.trendingCards)

  useEffect(() => {
    if (!SUPABASE_CONFIGURED) return
    supabaseBrowser()
      .rpc('get_trending_content', { p_days: 7, p_limit: 40 })
      .then(({ data }) => {
        const rows = (data as TrendingRow[] | null) || []
        if (!rows.length) return
        const buckets: TrendingRow[][] = [
          rows.filter((r) => isHub(r.content_path)),
          rows.filter((r) => isStage(r.content_path)),
          rows.filter(isEquipment),
        ]
        setCards((prev) =>
          prev.map((card, i) => {
            const bucket = buckets[i] || []
            if (bucket.length < 2) return card
            return {
              ...card,
              links: bucket
                .slice(0, LINKS_PER_CARD)
                .map((r) => ({ label: displayTitle(r), href: r.content_path })),
            }
          }),
        )
      })
  }, [])

  return <TrendingNow {...props} trendingCards={cards} />
}
